import React, {useEffect, useRef} from 'react';
import {usePathname} from '@/hooks/useAppRouter';
import {useAtomValue, useSetAtom} from 'jotai';
// dynamic import available for code-splitting when needed
import styles from './style.module.css';
import {Header} from '@/components/layout/header';
import {Navigation} from '@/components/layout/navigation';
import {ToastContainer} from '@/components/ui/toast';
import {lindaChatOpenAtom, lindaChatWidthAtom} from '@/store/linda-chat';
import {LindaChatPanel} from '@/components/linda-chat-panel';
import {usePermissions} from '@/hooks/usePermissions';

// TEMPORARY: Direct import to bypass dynamic import caching issue
// Load Linda chat dynamically (ssr: false required for Jotai hydration)
// const LindaChatPanel = dynamic(
//     () => import('@/components/linda-chat-panel').then((mod) => ({default: mod.LindaChatPanel})),
//     {ssr: false}
// );
import {productsAtom} from '@/store/products';
import {groupsSearchParamsAtom} from '@/store/groups';

export function Layout({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const pathname = usePathname();
    const prevPathnameRef = useRef<string | null>(null);
    const isChatOpen = useAtomValue(lindaChatOpenAtom);
    const chatWidth = useAtomValue(lindaChatWidthAtom);
    const {hasAccess} = usePermissions();
    const lindaVisible = hasAccess('view-linda') && isChatOpen;

    const setProductsState = useSetAtom(productsAtom);
    const setGroupsSearchParams = useSetAtom(groupsSearchParamsAtom);

    useEffect(() => {
        const prevPath = prevPathnameRef.current;

        // Skip on initial mount
        if (prevPath === null) {
            prevPathnameRef.current = pathname;
            return;
        }

        // Only clear if we're actually changing routes
        if (prevPath === pathname) {
            return;
        }

        const wasOnProducts = prevPath.startsWith('/products');
        const wasOnGroups = prevPath.startsWith('/groups');

        const isOnProducts = pathname.startsWith('/products');
        const isOnGroups = pathname.startsWith('/groups');

        // Clear products search when leaving products pages
        if (wasOnProducts && !isOnProducts) {
            setProductsState((prev) => ({
                ...prev,
                query: '',
                page: 0,
                items: [],
                total: 0,
                initialized: false,
            }));
        }

        // Clear groups search when leaving groups pages
        if (wasOnGroups && !isOnGroups) {
            setGroupsSearchParams((prev) => ({
                ...prev,
                name: '',
                page: 1,
            }));
        }

        prevPathnameRef.current = pathname;
    }, [pathname, setProductsState, setGroupsSearchParams]);

    return (
        <div
            className={`${styles.container} ${lindaVisible ? styles.chatOpen : ''}`}
            style={{
                paddingRight: lindaVisible
                    ? `calc(${chatWidth}px + var(--fp-container-spacing))`
                    : 'var(--fp-container-spacing)',
            }}
        >
            <Header />
            <Navigation />
            <main className={styles.main}>{children}</main>
            <ToastContainer />
            <LindaChatPanel />
        </div>
    );
}
